import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useState,
} from "react";
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, Star, Loader2 } from "lucide-react";
import StockLevelBar from "./StockLevelBar";
import { Product } from "../types";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import ProductImageWithHover from "@/features/purchase-orders/components/ProductImageWithHover";
import { IncomingHoverCard } from "./IncomingHoverCard";
import { useRole } from "@/hooks/use-role";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VelocityBar } from "@/features/reporting/components/consumption/VelocityBar";
import { availableStock, backupFigure, justInTimeSource, supplyPoolFigure } from "../utils/stockUtils";
import { backupJustInTimeLine, backupSourceName, backupSplitLine } from "../utils/backupStockCopy";
import { JustInTimeStock } from "./JustInTimeStock";
import { ReservedForBadge } from "@/features/base-garments/components/ReservedForBadge";

type SortField = 'sku' | 'type' | 'color' | 'size' | 'stockLevel' | 'velocity';
type SortDirection = 'asc' | 'desc';

const quantityFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const velocityFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function formatCoverDays(days: number): string {
  return days > 999 ? '999+' : quantityFormatter.format(days);
}

interface InventoryTableListProps {
  products: Product[];
  loading: boolean;
  selectedProducts: Set<string>;
  sortField: SortField;
  sortDirection: SortDirection;
  onSortChange: (field: SortField) => void;
  /**
   * Toggles one row. Pass the originating event so a shift-click can extend
   * the selection from the table's anchor row (handled in InventoryTable).
   */
  onCheckboxChange: (productId: string, event?: { shiftKey?: boolean }) => void;
  onSelectAll: () => void;
  /** Escape inside the table drops the whole selection. */
  onClearSelection: () => void;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  onRowClick: (product: Product) => void;
  velocityBySkuId: Map<string, { velocity: number; units: number }>;
  velocityLoading: boolean;
  velocityUnavailable: boolean;
  velocityRangeDays: number;
  maxVelocity: number;
  // BLU-3xx Phase 47.01.2-B-03: cross-org provenance treatments. When the
  // shared-locations flag is on AND multiple orgs are present in the data
  // AND the per-client filter is "All clients", show the owner column +
  // brand-purple left-edge ribbon (§13.3) on rows where the owner is NOT
  // the caller. When the filter narrows to one client, drop both — the
  // sticky banner above the table carries the meaning.
  showOwnerColumn?: boolean;
  ownOrgId?: string;
  /** BLU-1458: the caller's org name, for the back-up split ("0 owned by ..."). */
  ownOrgName?: string;
  orgIdToName?: Map<string, string>;
  /** BLU-1394 F25: names the client on a blank's "Only <client>" badge. */
  reservedClientName?: (clientOrganizationId: string) => string | null;
}

export function InventoryTableList({
  products,
  loading,
  selectedProducts,
  sortField,
  sortDirection,
  onSortChange,
  onCheckboxChange,
  onSelectAll,
  onClearSelection,
  currentPage,
  itemsPerPage,
  totalItems,
  totalPages,
  onPageChange,
  onItemsPerPageChange,
  onRowClick,
  velocityBySkuId,
  velocityLoading,
  velocityUnavailable,
  velocityRangeDays,
  maxVelocity,
  showOwnerColumn = false,
  ownOrgId,
  ownOrgName,
  orgIdToName,
  reservedClientName,
}: InventoryTableListProps) {
  const { t } = useTranslation('inventory');
  const { can } = useRole();
  const isAdmin = can('skus.manage');
  const utils = trpc.useUtils();

  // Optimistic key-item toggles: skuId -> pending isKeyItem value
  const [pendingKeyItems, setPendingKeyItems] = useState<Map<string, boolean>>(new Map());

  const setKeyItemMutation = trpc.inventory.setKeyItem.useMutation({
    onSuccess: async (_data, variables) => {
      await utils.inventory.getProducts.invalidate();
      setPendingKeyItems((prev) => {
        const next = new Map(prev);
        next.delete(variables.skuId);
        return next;
      });
    },
    onError: (error, variables) => {
      setPendingKeyItems((prev) => {
        const next = new Map(prev);
        next.delete(variables.skuId);
        return next;
      });
      toast.error('Failed to update key item status', {
        description: error.message,
      });
    },
  });

  const handleToggleKeyItem = useCallback((productId: string, currentIsKeyItem: boolean) => {
    const newValue = !currentIsKeyItem;
    setPendingKeyItems((prev) => new Map(prev).set(productId, newValue));
    setKeyItemMutation.mutate({ skuId: productId, isKeyItem: newValue });
  }, [setKeyItemMutation]);

  // Compute dynamic colSpan for empty state. The operational list deliberately
  // omits valuation and accounting-detail columns so its primary stock signals
  // remain readable at laptop widths.
  const baseColCount = 11;
  const colSpan =
    (isAdmin ? baseColCount + 1 : baseColCount) +
    (showOwnerColumn ? 1 : 0);

  // Helper function to render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc'
      ? <ArrowUp className="inline h-3 w-3 ml-1" />
      : <ArrowDown className="inline h-3 w-3 ml-1" />;
  };

  const handleCheckboxChange = useCallback((
    productId: string,
    event?: { shiftKey?: boolean },
  ) => {
    onCheckboxChange(productId, event);
  }, [onCheckboxChange]);

  const handleSelectAll = useCallback(() => {
    onSelectAll();
  }, [onSelectAll]);

  // Once anything is selected the table is in selection mode: a row click
  // toggles instead of opening the detail modal. With nothing selected the
  // row click behaves exactly as it always has.
  const selectionActive = selectedProducts.size > 0;
  const allOnPageSelected = products.length > 0 && products.every(p => selectedProducts.has(p.id));
  const someOnPageSelected = !allOnPageSelected && products.some(p => selectedProducts.has(p.id));

  const handleRowActivate = useCallback((
    product: Product,
    event: { shiftKey?: boolean },
  ) => {
    if (selectionActive) {
      onCheckboxChange(product.id, event);
      return;
    }
    onRowClick(product);
  }, [onCheckboxChange, onRowClick, selectionActive]);

  // Escape clears the selection whenever focus sits inside the table. Scoped
  // to this container (not the document) so it never competes with a modal's
  // own Escape handling.
  const handleContainerKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || !selectionActive) return;
    event.stopPropagation();
    onClearSelection();
  }, [onClearSelection, selectionActive]);

  return (
    <>
      {/* Product Table */}
      {/* BLU-411: neutralise shadcn Table's inner `overflow-auto` wrapper
          ([&>div]) so `position: sticky` on the header cells escapes to the
          real scroll container (<SidebarInset>, per DESIGN.md §6) instead of
          being trapped by the wrapper. The page owns horizontal scroll. */}
      {/* `select-none` while a selection is live: shift-clicking rows would
          otherwise smear a text highlight across the table. */}
      <div
        className={`rounded-md border [&>div]:overflow-visible ${selectionActive ? 'select-none' : ''}`}
        onKeyDown={handleContainerKeyDown}
      >
        <Table>
          {/* BLU-411: pin the column headers just beneath the sticky
              PageActionBar. Offset = TopHeader height + the bar's measured
              height (--page-action-bar-height, published by PageActionBar).
              z-10 keeps headers above body rows but below the bar (z-40);
              solid bg + inset bottom border stop rows bleeding through. */}
          <TableHeader
            className="[&_th]:sticky [&_th]:top-[var(--inventory-header-top)] [&_th]:z-10 [&_th]:whitespace-nowrap [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]"
            style={{
              ['--inventory-header-top' as string]:
                'calc(var(--top-header-height, 3.5rem) + var(--page-action-bar-height, 0px))',
            } as CSSProperties}
          >
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    allOnPageSelected
                      ? true
                      : someOnPageSelected
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={handleSelectAll}
                  aria-label={t('table.selectAllOnPage')}
                />
              </TableHead>
              {isAdmin && <TableHead className="w-8 px-1" />}
              {showOwnerColumn && (
                <TableHead scope="col" className="w-[140px]">
                  Owner
                </TableHead>
              )}
              <TableHead className="w-20">{t('table.image')}</TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/30"
                onClick={() => onSortChange('sku')}
              >
                {t('table.sku')} {renderSortIndicator('sku')}
              </TableHead>
              <TableHead>{t('table.title')}</TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/30"
                onClick={() => onSortChange('type')}
              >
                {t('table.type')} {renderSortIndicator('type')}
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/30 max-w-[100px]"
                onClick={() => onSortChange('color')}
              >
                {t('table.color')} {renderSortIndicator('color')}
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/30"
                onClick={() => onSortChange('size')}
              >
                {t('table.size')} {renderSortIndicator('size')}
              </TableHead>
              <TableHead
                className="cursor-pointer hover:bg-muted/30"
                onClick={() => onSortChange('stockLevel')}
              >
                {t('table.available')} {renderSortIndicator('stockLevel')}
              </TableHead>
              <TableHead
                className="min-w-[112px] cursor-pointer hover:bg-muted/30"
                onClick={() => onSortChange('velocity')}
              >
                <span>{t('table.velocity')}</span>
                {renderSortIndicator('velocity')}
                <span className="mt-0.5 block text-[10px] font-normal text-muted-foreground">
                  {t('table.velocityPeriod', { count: velocityRangeDays })}
                </span>
              </TableHead>
              <TableHead className="text-right">{t('table.incoming')}</TableHead>
              <TableHead className="text-right">{t('table.alertAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`loading-${i}`}>
                  <TableCell>
                    <Skeleton className="h-4 w-4 rounded" />
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="w-8 px-1">
                      <Skeleton className="h-3.5 w-3.5 rounded" />
                    </TableCell>
                  )}
                  {showOwnerColumn && (
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  )}
                  <TableCell className="p-2">
                    <Skeleton className="h-12 w-12 rounded" />
                  </TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-12 ml-auto" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-10 ml-auto" /></TableCell>
                </TableRow>
              ))
            ) : products.length > 0 ? (
              products.map((product) => {
                const available = availableStock(product);
                const poolFigure = supplyPoolFigure(product);
                const backup = backupFigure(product);
                const backupLine = backup > 0
                  ? backupSplitLine(t, {
                      backup,
                      owner: backupSourceName(t, product),
                      own: available,
                      ownOrgName,
                    })
                  : null;
                // BLU-1268: Just in time (its own pair, the 3PL's pool behind an
                // alias, or the 3PL's back-up) is not a count: the cell says so.
                const justInTime = justInTimeSource(product);
                const title = product.title?.trim() || product.category.type || '-';
                // ace/trims-ux: type badge label — reuses the same wording the
                // Products page badge uses ('Trim' overrides 'POD'/'PAP' — see
                // Products.tsx's variants.every(isTrim) badge).
                const typeBadgeLabel = product.isTrim ? 'Trim' : product.productionType;

                // BLU-3xx Phase 47.01.2-B-03: cross-org provenance per row.
                // The ribbon (§13.3 brand-purple left edge) ONLY renders for
                // client-owned rows when the unfiltered Owner column is in
                // play — own-org rows look "normal" (home turf) so the eye-
                // grabbing treatment is reserved for the cross-org case.
                // Edit affordances are NOT disabled here at the row level
                // beyond what the existing `stockActionsDisabled` prop drives;
                // data-layer correctness is enforced server-side via RLS.
                const rowOwnerOrgId = product.ownerOrganizationId;
                const isPartnerOwned =
                  !!rowOwnerOrgId &&
                  !!ownOrgId &&
                  rowOwnerOrgId !== ownOrgId;
                const isClientOwned = showOwnerColumn && isPartnerOwned;
                const ownerName =
                  rowOwnerOrgId
                    ? (orgIdToName?.get(rowOwnerOrgId) ?? `Partner ${rowOwnerOrgId.slice(0, 6)}…`)
                    : '-';
                const velocityRecord = velocityBySkuId.get(product.id);
                const velocity = isPartnerOwned || velocityUnavailable
                  ? null
                  : (velocityRecord?.velocity ?? 0);
                const coverDays = velocity !== null && velocity > 0
                  ? Math.max(0, Math.round(available / velocity))
                  : null;

                return (
                <TableRow
                  key={product.id}
                  className={`cursor-pointer ${isClientOwned ? 'border-l-4 border-l-threadheads-purple/30' : ''}`}
                  data-state={selectedProducts.has(product.id) ? 'selected' : undefined}
                  tabIndex={0}
                  // Shift-drag would otherwise select the row text instead of
                  // the rows. Focus is restored by hand since preventDefault
                  // on mousedown also swallows it.
                  onMouseDown={(event) => {
                    if (!event.shiftKey) return;
                    event.preventDefault();
                    event.currentTarget.focus();
                  }}
                  onClick={(event) => handleRowActivate(product, event)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleRowActivate(product, event);
                    }
                  }}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedProducts.has(product.id)}
                      onClick={(event) => {
                        event.stopPropagation();
                        // Radix fires onCheckedChange from its own click
                        // handler, which carries no event — intercept the
                        // shift-click here and drive the range directly.
                        if (event.shiftKey) {
                          event.preventDefault();
                          handleCheckboxChange(product.id, event);
                        }
                      }}
                      onKeyDown={(event) => event.stopPropagation()}
                      onCheckedChange={() => handleCheckboxChange(product.id)}
                      aria-label={`Select ${product.sku}`}
                    />
                  </TableCell>
                  {isAdmin && (() => {
                    const isPending = pendingKeyItems.has(product.id);
                    const isKeyItem = isPending
                      ? pendingKeyItems.get(product.id)!
                      : Boolean(product.isKeyItem);
                    return (
                      <TableCell className="w-8 px-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded transition-transform duration-150 hover:scale-110"
                                disabled={isPending}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleKeyItem(product.id, isKeyItem);
                                }}
                                onKeyDown={(event) => {
                                  event.stopPropagation();
                                }}
                                aria-label={isKeyItem ? 'Remove key item status' : 'Mark as key item'}
                              >
                                {isPending ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-500" />
                                ) : (
                                  <Star
                                    className={`h-3.5 w-3.5 ${
                                      isKeyItem
                                        ? 'fill-amber-500 text-amber-500'
                                        : 'text-muted-foreground/40'
                                    }`}
                                  />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{isKeyItem ? 'Remove key item status' : 'Mark as key item: triggers Slack alerts when out of stock'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    );
                  })()}
                  {showOwnerColumn && (
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className={`text-xs truncate inline-block max-w-[140px] ${isClientOwned ? 'text-threadheads-purple font-medium' : 'text-muted-foreground'}`}
                            >
                              {ownerName}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{ownerName}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  )}
                  <TableCell className="p-2">
                    <ProductImageWithHover
                      imageUrl={product.image_url}
                      productSku={product.sku}
                      size="large"
                    />
                  </TableCell>
                  <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="truncate">{title}</span>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {typeBadgeLabel}
                      </Badge>
                      {product.reservedForOrganizationId && (
                        <ReservedForBadge
                          clientName={reservedClientName?.(product.reservedForOrganizationId) ?? null}
                          className="shrink-0"
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{product.category.type || "-"}</TableCell>
                  <TableCell className="max-w-[100px] truncate" title={product.category.color || undefined}>{product.category.color || "-"}</TableCell>
                  <TableCell>{product.category.size || "-"}</TableCell>
                  <TableCell>
                    {justInTime === 'own' ? (
                      <JustInTimeStock />
                    ) : justInTime === 'pool' ? (
                      <JustInTimeStock
                        linked
                        line={t('sharedSkus.supply.poolLine', { owner: product.supplyAlias?.ownerName ?? '' })}
                      />
                    ) : justInTime === 'backup' ? (
                      <JustInTimeStock
                        linked
                        line={backupJustInTimeLine(t, {
                          owner: backupSourceName(t, product),
                          own: available,
                          ownOrgName,
                        })}
                      />
                    ) : (
                      <>
                      {/* BLU-711 / BLU-1458: owner-pool units reachable via a
                          shared-SKU mapping (on All Locations, summed across
                          the client's floors that hold the row) count as the
                          row's stock: the bar shows the total, orange when
                          back-up alone covers it, the split on hover. */}
                      <StockLevelBar
                        currentStock={poolFigure ?? available}
                        maxStock={100}
                        notifyLevel={product.notify}
                        backup={backup}
                        backupLabel={backupLine}
                      />
                      {/* Phase 92 (UI-SPEC 1.6): a client's alias row holds no
                          stock of its own, so the cell shows the 3PL's shared
                          pool (before hold-back) and says whose stock it is,
                          never the row's own zero. */}
                      {poolFigure !== null && product.supplyAlias?.ownerName && (
                        <div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                          {t('sharedSkus.supply.poolLine', { owner: product.supplyAlias.ownerName })}
                        </div>
                      )}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="min-w-[112px]">
                    {velocityLoading && !isPartnerOwned ? (
                      <Skeleton className="h-8 w-20" />
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-24 space-y-1">
                              <div className="flex items-baseline justify-between gap-2 tabular-nums">
                                <span className={`text-sm font-medium ${velocity === null ? 'text-muted-foreground' : ''}`}>
                                  {velocity === null
                                    ? '-'
                                    : t('table.perDay', { value: velocityFormatter.format(velocity) })}
                                </span>
                                {coverDays !== null && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {t('table.coverDays', { days: formatCoverDays(coverDays) })}
                                  </span>
                                )}
                              </div>
                              <VelocityBar value={velocity ?? 0} max={maxVelocity} />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {velocity === null
                              ? t('table.velocityUnavailable')
                              : velocity === 0
                                ? t('table.noConsumption', { count: velocityRangeDays })
                                : t('table.velocityTooltip', {
                                    units: quantityFormatter.format(velocityRecord?.units ?? 0),
                                    count: velocityRangeDays,
                                    cover: formatCoverDays(coverDays ?? 0),
                                  })}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {(product.incoming ?? 0) > 0 ? (
                      <IncomingHoverCard
                        skuId={product.id}
                        incoming={product.incoming ?? 0}
                      />
                    ) : (
                      0
                    )}
                  </TableCell>
                  <TableCell className={`text-right tabular-nums ${!product.notify ? 'text-muted-foreground' : ''}`}>
                    {product.notify ? product.notify : '-'}
                  </TableCell>
                </TableRow>
              )})
            ) : (
              <TableRow>
                <TableCell colSpan={colSpan} className="h-24 text-center">
                  {t('table.noProductsFound')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>
              {t('table.showingPagination', { from: ((currentPage - 1) * itemsPerPage) + 1, to: Math.min(currentPage * itemsPerPage, totalItems), total: totalItems })}
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                onItemsPerPageChange(Number(e.target.value));
                onPageChange(1);
              }}
              className="ml-2 border rounded px-2 py-1 text-sm"
            >
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={200}>200 per page</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
            >
              {t('table.first')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {t('table.previous')}
            </Button>

            <span className="px-3 py-1 text-sm bg-gray-100 rounded">
              {t('table.page', { current: currentPage, total: totalPages })}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t('table.next')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              {t('table.last')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
